import React from 'react';
import { getShopSubscription } from '../../modules/shop'
import { Link as RouterLink } from 'react-router-dom'
import { bindActionCreators } from 'redux'
import { connect } from 'react-redux'
import withStyles from '../../withStylesAdapter';
import { Paper, Grid } from '@mui/material';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import Button from '@mui/material/Button';
import Avatar from '@mui/material/Avatar';
import Link from '@mui/material/Link';
import Chip from '@mui/material/Chip';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import Tooltip from '@mui/material/Tooltip';
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined';
import TranslateOutlinedIcon from '@mui/icons-material/TranslateOutlined';
import ConfirmationNumberOutlinedIcon from '@mui/icons-material/ConfirmationNumberOutlined';
import { withTranslation } from 'react-i18next';
import API from '../../api/api';

import RechargePlans from '../../components/RechargePlans';
import { formatDate, formatLargeNumber } from '../../helper.js';
import upperFirst from 'lodash/upperFirst';
import { hasBulkTranslationAccess } from '../../modules/config';

const styles = theme => ({
  paper: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    padding: 24,
  },
  cardTitle: {
    marginBottom: 16,
  },
  planTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
  },
  // the plan picture of the subscription page, with its dark-mode treatment (PricingPlans media)
  planImage: {
    width: 44,
    height: 44,
    ...(theme.palette.mode === 'dark' && {
      filter: 'invert(1) hue-rotate(180deg)',
      mixBlendMode: 'screen',
    }),
  },
  storeBar: {
    display: 'flex',
    alignItems: 'center',
    gap: 20,
    padding: '16px 24px',
  },
  avatarIcon: {
    width: '4rem',
    height: '4rem',
    fontSize: '1.6rem',
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
  },
  // label | value rows instead of the timeline: three facts do not need a diagram
  factRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 16,
    padding: '8px 0',
    borderBottom: `1px solid ${theme.palette.divider}`,
    '&:last-of-type': {
      borderBottom: 'none',
    },
  },
  statusChip: {
    textTransform: 'capitalize',
  },
  wordsLine: {
    display: 'flex',
    alignItems: 'baseline',
    gap: 8,
    marginBottom: 4,
  },
  actions: {
    marginTop: 'auto',
    paddingTop: 16,
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'flex-end',
    gap: 8,
  },
  featureButton: {
    justifyContent: 'flex-start',
    textTransform: 'none',
  },
});

class Profile extends React.Component {

  constructor(props) {
    super(props);

    this.state = {
      showRechargePlans: false,
    }
  }

  componentDidMount() {
    this.props.getShopSubscription();
  }

  onBuyWords = (plan) => {
    API.buyWords(plan, () => {});
  }

  renderStoreBar() {
    const { classes, shopUrl } = this.props;

    return (
      <Paper className={classes.storeBar}>
        {/* same letters as the avatar in the header */}
        <Avatar className={classes.avatarIcon}>
          {shopUrl.substring(0, 2).toUpperCase()}
        </Avatar>
        <div>
          <Typography variant="h5" component="h3">
            {shopUrl.replace('.myshopify.com', '')}
          </Typography>
          <Typography variant="body2">
            <Link href={`https://${shopUrl}`} target="_blank" rel="noopener noreferrer">{shopUrl}</Link>
          </Typography>
        </div>
      </Paper>
    );
  }

  renderSubscriptionCard() {
    const { t, classes, charge, subscription } = this.props;
    const isFree = charge.name === 'free';
    const active = charge.status === 'active' || charge.status === 'accepted';

    return (
      <Paper className={classes.paper}>
        <Typography variant="h6" component="h3" className={`${classes.cardTitle} ${classes.planTitle}`} sx={{ textTransform: 'capitalize' }}>
          <img src={`/files/images/${charge.name}.webp`} alt="" className={classes.planImage} />
          {t('profile.page.planName', { plan: charge.name })}
        </Typography>
        {isFree ?
          <Typography variant="body2" color="text.secondary">
            {t('profile.page.freePlanHint')}
          </Typography>
        :
          <div>
            <div className={classes.factRow}>
              <Typography variant="body2" color="text.secondary">{t('profile.page.status')}</Typography>
              <Chip
                size="small"
                className={classes.statusChip}
                icon={<FiberManualRecordIcon sx={{ '&&': { color: active ? 'success.main' : 'error.main' } }} />}
                label={charge.status}
              />
            </div>
            {subscription.length > 0 &&
              <div className={classes.factRow}>
                <Typography variant="body2" color="text.secondary">{t('profile.page.nextBilling')}</Typography>
                <Typography variant="body2">{formatDate(subscription[0].currentPeriodEnd)} · ${parseFloat(charge.price).toFixed(2)}</Typography>
              </div>
            }
            {charge.activated_on &&
              <div className={classes.factRow}>
                <Typography variant="body2" color="text.secondary">{t('profile.page.activeSince')}</Typography>
                <Typography variant="body2">{formatDate(charge.activated_on)}</Typography>
              </div>
            }
            {charge.created_at &&
              <div className={classes.factRow}>
                <Typography variant="body2" color="text.secondary">{t('profile.page.createdOn')}</Typography>
                <Typography variant="body2">{formatDate(charge.created_at)}</Typography>
              </div>
            }
          </div>
        }
        <div className={classes.actions}>
          <Button variant="outlined" color="primary" component={RouterLink} to="/profile/plans">
            {isFree ? t('profile.page.choosePlan') : t('profile.page.manageSubscription')}
          </Button>
        </div>
      </Paper>
    );
  }

  renderWordsCard() {
    const { t, classes, shop, charge } = this.props;
    const isFree = charge.name === 'free';
    const words = shop.auto_translations || {};
    const available = words.available_words > 0 ? words.available_words : 0;

    return (
      <Paper className={classes.paper}>
        <Typography variant="h6" component="h3" className={classes.cardTitle}>
          <TranslateOutlinedIcon sx={{ verticalAlign: 'text-bottom', mr: 1 }} />
          {t('profile.page.words')}
        </Typography>
        {isFree ?
          <Typography variant="body2" color="text.secondary">
            {t('profile.page.wordsNeedPlan')}
          </Typography>
        :
          <React.Fragment>
            <Typography component="p" className={classes.wordsLine}>
              <Typography component="span" variant="h4" color={available < 50 ? 'error.main' : 'success.main'} sx={{ fontWeight: 500 }}>
                {formatLargeNumber(available)}
              </Typography>
              <Typography component="span" variant="body1" color="text.secondary">
                {t('profile.page.wordsLeft')}
              </Typography>
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('profile.page.wordsUsed', { count: formatLargeNumber(words.total_used_words || 0) })}
            </Typography>
          </React.Fragment>
        }
        <div className={classes.actions}>
          {isFree ?
            <Button variant="outlined" color="primary" component={RouterLink} to="/profile/plans">
              {t('profile.page.choosePlan')}
            </Button>
          :
            <Button variant="contained" color="primary" onClick={() => this.setState({ showRechargePlans: true })}>
              {t('itemsList.buyMoreWords')}
            </Button>
          }
        </div>
      </Paper>
    );
  }

  // words log needs a paid plan, tickets premium; bulk comes with growth and premium and is switched
  // on by hand for basic via the auto_translation_bulk feature flag
  renderToolsCard() {
    const { t, classes, charge, shop } = this.props;
    const paid = charge.name !== 'free';
    const bulkUnlocked = hasBulkTranslationAccess(charge.name, shop.langify_features);
    const tools = [
      { key: 'wordsLog', icon: <HistoryOutlinedIcon />, label: t('profile.navigation.wordsLog'), to: '/machinetranslation/wordslog', enabled: paid, neededPlan: 'basic' },
      { key: 'bulk', icon: <TranslateOutlinedIcon />, label: t('navigation.primary.bulkSettings'), to: '/machinetranslation/bulk', enabled: bulkUnlocked, neededPlan: 'growth' },
      { key: 'support', icon: <ConfirmationNumberOutlinedIcon />, label: t('profile.navigation.supportTickets'), to: '/profile/tickets', enabled: charge.name === 'premium', neededPlan: 'premium' },
    ];

    return (
      <Paper className={classes.paper}>
        <Typography variant="h6" component="h3" className={classes.cardTitle}>
          {t('profile.page.tools')}
        </Typography>
        <div className={classes.actions} style={{ marginTop: 0, paddingTop: 0, flexDirection: 'column', alignItems: 'stretch' }}>
          {tools.map(tool =>
            tool.enabled ?
              <Button key={tool.key} variant="outlined" color="primary" className={classes.featureButton} startIcon={tool.icon} component={RouterLink} to={tool.to}>
                {tool.label}
              </Button>
            :
              // a disabled button fires no events, so the tooltip needs the wrapper
              <Tooltip key={tool.key} title={t('profile.page.needsPlan', { plan: upperFirst(tool.neededPlan) })} arrow placement="left">
                <span style={{ display: 'flex' }}>
                  <Button variant="outlined" disabled className={classes.featureButton} startIcon={tool.icon} fullWidth>
                    {tool.label}
                  </Button>
                </span>
              </Tooltip>
          )}
        </div>
      </Paper>
    );
  }

  render() {
    const { t } = this.props;
    const cardSize = { xs: 12, md: 4 };

    return <React.Fragment>
      <Grid container spacing={3} alignItems="stretch" className="page-content">
        <Grid size={12} className="page-content">
          <Typography variant="h4" component="h2">{t('profile.page.title')}</Typography>
          <Divider className="divider-white" />
        </Grid>
        <Grid size={12}>
          {this.renderStoreBar()}
        </Grid>
        <Grid size={cardSize}>
          {this.renderSubscriptionCard()}
        </Grid>
        <Grid size={cardSize}>
          {this.renderWordsCard()}
        </Grid>
        <Grid size={cardSize}>
          {this.renderToolsCard()}
        </Grid>
      </Grid>
      <RechargePlans
        visible={this.state.showRechargePlans}
        onClose={() => this.setState({ showRechargePlans: false })}
        onBuy={this.onBuyWords}
      />
    </React.Fragment>
  }
}
const mapStateToProps = ({ shop }) => ({
  shop: shop.shop,
  shopUrl: shop.shop.url,
  charge: shop.shop.charge,
  subscription: shop.subscription || []
})

const mapDispatchToProps = dispatch =>
  bindActionCreators(
    {
      getShopSubscription
    },
    dispatch
  )

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withStyles(styles)(withTranslation()(Profile)))
